import {
  circle,
  ellipse,
  fill,
  flat,
  INK,
  line,
  P,
  poly,
  polyline,
  rect,
  shine,
  type IconDef,
  type Shape,
} from '../kit';

/*
 * Geometry helpers. The kit has no transforms, so mirroring, stretching, hearts, stars and the
 * merged outlines (puffs, curls, beards) are worked out here. `xf` maps absolute commands only
 * and leaves relative ones alone, so it can mirror or scale only our own absolute paths (never
 * circle()/ellipse()/rect()), while a plain move (`shift`) is safe for any path.
 */

type Pt = [number, number];
/** A circle as [cx, cy, r]. */
type Ball = readonly [number, number, number];

const r2 = (v: number): number => Math.round(v * 100) / 100;
const pt = (p: readonly number[]): string => `${r2(p[0])} ${r2(p[1])}`;

/**
 * Moves every absolute point of a path through f (H and V assume f keeps axes apart); arcs get
 * their radii scaled by k and their sweep flipped for mirrors.
 */
function xf(d: string, f: (x: number, y: number) => Pt, k = 1, mirror = false): string {
  return d.replace(/([MLCQSTAHV])([^A-Za-z]*)/g, (_m, cmd: string, args: string) => {
    const v = (args.match(/-?\d*\.?\d+/g) ?? []).map(Number);
    const out: string[] = [];
    if (cmd === 'H' || cmd === 'V') {
      for (const a of v) out.push(String(r2(cmd === 'H' ? f(a, 0)[0] : f(0, a)[1])));
    } else if (cmd === 'A') {
      for (let i = 0; i + 6 < v.length; i += 7) {
        const sweep = mirror ? 1 - v[i + 4] : v[i + 4];
        out.push(
          `${r2(v[i] * k)} ${r2(v[i + 1] * k)} ${v[i + 2]} ${v[i + 3]} ${sweep} ${pt(f(v[i + 5], v[i + 6]))}`,
        );
      }
    } else {
      for (let i = 0; i + 1 < v.length; i += 2) out.push(pt(f(v[i], v[i + 1])));
    }
    return cmd + out.join(' ');
  });
}

/** Mirror image across the middle of the box. */
const mx = (d: string): string => xf(d, (x, y) => [32 - x, y], 1, true);
/** A path plus its mirror image, as one path. */
const both = (d: string): string => d + mx(d);
/** Rotates one point (for circle centers). */
const turn = (x: number, y: number, deg: number, cx = 16, cy = 16): Pt => {
  const a = (deg * Math.PI) / 180;
  return [
    cx + (x - cx) * Math.cos(a) - (y - cy) * Math.sin(a),
    cy + (x - cx) * Math.sin(a) + (y - cy) * Math.cos(a),
  ];
};
/** Moves a whole icon by (dx, dy), to center it in the box. */
const shift = (dx: number, dy: number, shapes: Shape[]): Shape[] =>
  shapes.map((s) => ({ ...s, d: xf(s.d, (x, y) => [x + dx, y + dy]) }));
/** Stretches a path vertically by k around the line y = cy. */
const stretch = (d: string, k: number, cy = 16): string => xf(d, (x, y) => [x, cy + (y - cy) * k]);
/** Scales a path drawn around (16, 16) by k and moves it to (cx, cy). */
const place = (d: string, cx: number, cy: number, k: number): string =>
  xf(d, (x, y) => [cx + (x - 16) * k, cy + (y - 16) * k], k);

/** The heart from the symbols set, so the hearts here match it. */
const HEART =
  'M16 27C10 22.4 4 18.2 4 11.6C4 7.6 7 5 10.3 5C12.8 5 14.8 6.4 16 8.6C17.2 6.4 19.2 5 21.7 5C25 5 28 7.6 28 11.6C28 18.2 22 22.4 16 27Z';
const heart = (cx: number, cy: number, w: number): string => place(HEART, cx, cy, w / 24);

/** A five-point star. */
function star(cx: number, cy: number, R: number, r = R * 0.48): string {
  const p: number[] = [];
  for (let i = 0; i < 10; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    const k = i % 2 ? r : R;
    p.push(cx + k * Math.cos(a), cy + k * Math.sin(a));
  }
  return poly(...p);
}

/** A four-point sparkle with pinched sides. */
function sparkle(cx: number, cy: number, R: number): string {
  const k = R * 0.22;
  return (
    `M${pt([cx, cy - R])}Q${pt([cx + k, cy - k])} ${pt([cx + R, cy])}Q${pt([cx + k, cy + k])} ${pt([cx, cy + R])}` +
    `Q${pt([cx - k, cy + k])} ${pt([cx - R, cy])}Q${pt([cx - k, cy - k])} ${pt([cx, cy - R])}Z`
  );
}

/** A water drop, round part of radius r centered on (cx, cy), tip up. */
const drop = (cx: number, cy: number, r: number): string =>
  `M${pt([cx, cy - 2 * r])}C${pt([cx + 0.5 * r, cy - 1.3 * r])} ${pt([cx + r, cy - 0.8 * r])} ${pt([cx + r, cy])}` +
  `A${r2(r)} ${r2(r)} 0 0 1 ${pt([cx - r, cy])}C${pt([cx - r, cy - 0.8 * r])} ${pt([cx - 0.5 * r, cy - 1.3 * r])} ${pt([cx, cy - 2 * r])}Z`;

/** A chunky letter Z, s across, strokes t thick, top-left corner at (x, y). */
function zee(x: number, y: number, s: number, t: number): string {
  const d = t * 1.5;
  const corners: Pt[] = [
    [0, 0],
    [s, 0],
    [s, t],
    [d, s - t],
    [s, s - t],
    [s, s],
    [0, s],
    [0, s - t],
    [s - d, t],
    [0, t],
  ];
  return poly(...corners.flatMap(([cx, cy]) => [x + cx, y + cy]));
}

/** A spiral eye about 6 across, centered on (cx, cy); s = -1 winds the other way. */
function spiral(cx: number, cy: number, s: 1 | -1): string {
  const x = cx - s;
  const y = cy + 0.5;
  const sw = s > 0 ? 1 : 0;
  return `M${pt([x, y])}A1 1 0 0 ${sw} ${pt([x + 2 * s, y])}A2 2 0 0 ${sw} ${pt([x - 2 * s, y])}A3 3 0 0 ${sw} ${pt([x + 4 * s, y])}`;
}

/** A capsule from (x0, y0) to (x1, y1), r on each side: fingers, thumbs, blades. */
function pill(x0: number, y0: number, x1: number, y1: number, r: number): string {
  const l = Math.hypot(x1 - x0, y1 - y0);
  const nx = (-(y1 - y0) / l) * r;
  const ny = ((x1 - x0) / l) * r;
  const a = `A${r2(r)} ${r2(r)} 0 0 0`;
  return `M${pt([x0 + nx, y0 + ny])}L${pt([x1 + nx, y1 + ny])}${a} ${pt([x1 - nx, y1 - ny])}L${pt([x0 - nx, y0 - ny])}${a} ${pt([x0 + nx, y0 + ny])}Z`;
}

/** A scissor blade: wide at the pivot (px, py), pointed at the tip. */
function blade(px: number, py: number, tx: number, ty: number, w: number): string {
  const l = Math.hypot(tx - px, ty - py);
  const ux = (tx - px) / l;
  const uy = (ty - py) / l;
  return poly(
    px - ux * 1.2,
    py - uy * 1.2,
    px - uy * w,
    py + ux * w,
    tx,
    ty,
    px + uy * w * 0.6,
    py - ux * w * 0.6,
  );
}

/** A long almond nail from the fingertip (bx, by) out to its point (tx, ty), r on each side. */
function nail(bx: number, by: number, tx: number, ty: number, r: number): string {
  const l = Math.hypot(tx - bx, ty - by);
  const ux = (tx - bx) / l;
  const uy = (ty - by) / l;
  const nx = -uy * r;
  const ny = ux * r;
  const cx = bx + ux * l * 0.75;
  const cy = by + uy * l * 0.75;
  return `M${pt([bx + nx, by + ny])}Q${pt([cx + nx * 1.2, cy + ny * 1.2])} ${pt([tx, ty])}Q${pt([cx - nx * 1.2, cy - ny * 1.2])} ${pt([bx - nx, by - ny])}Z`;
}

/** A row of n rounded teeth across w from (x, y), each h tall. */
function teeth(x: number, y: number, w: number, n: number, h: number): string {
  const t = w / n;
  let d = '';
  for (let i = 0; i < n; i++) d += rect(x + i * t, y, t, h, 1.3);
  return d;
}

/** Where circles a and b cross: [left of the line a to b, right of it]. */
function cross(a: Ball, b: Ball): [Pt, Pt] {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const d = Math.hypot(dx, dy);
  const t = (a[2] * a[2] - b[2] * b[2] + d * d) / (2 * d);
  const h = Math.sqrt(Math.max(0, a[2] * a[2] - t * t));
  const x = a[0] + (t * dx) / d;
  const y = a[1] + (t * dy) / d;
  return [
    [x + (h * dy) / d, y - (h * dx) / d],
    [x - (h * dy) / d, y + (h * dx) / d],
  ];
}
/** A clockwise arc around circle c, from one point on it to another. */
function arcOn(c: Ball, from: Pt, to: Pt): string {
  let da = Math.atan2(to[1] - c[1], to[0] - c[0]) - Math.atan2(from[1] - c[1], from[0] - c[0]);
  while (da < 0) da += 2 * Math.PI;
  return `A${r2(c[2])} ${r2(c[2])} 0 ${da > Math.PI ? 1 : 0} 1 ${pt(to)}`;
}
/**
 * One outline around a row of overlapping circles: puffs, curls, beards, knuckles. With `ring`
 * the row closes into a loop (list the circles clockwise); otherwise both ends are rounded off.
 */
function bumps(cs: readonly Ball[], ring = false): string {
  const n = cs.length;
  if (ring) {
    const out = cs.map((c, i) => cross(c, cs[(i + 1) % n])[0]);
    let d = `M${pt(out[n - 1])}`;
    for (let i = 0; i < n; i++) d += arcOn(cs[i], out[(i + n - 1) % n], out[i]);
    return `${d}Z`;
  }
  const m = cs.slice(1).map((c, i) => cross(cs[i], c));
  let d = `M${pt(m[0][1])}${arcOn(cs[0], m[0][1], m[0][0])}`;
  for (let i = 1; i < n - 1; i++) d += arcOn(cs[i], m[i - 1][0], m[i][0]);
  d += arcOn(cs[n - 1], m[n - 2][0], m[n - 2][1]);
  for (let i = n - 2; i >= 1; i--) d += arcOn(cs[i], m[i][1], m[i - 1][1]);
  return `${d}Z`;
}
/** Mirrors an open row of circles across the middle of the box. */
const mballs = (cs: readonly Ball[]): Ball[] => cs.map(([x, y, r]): Ball => [32 - x, y, r]);

/** A thick outlined stroke through the points: limbs, straps, rifle stocks. */
const limb = (color: string, w: number, ...pts: number[]): Shape[] => [
  line(polyline(...pts), w + 3.2),
  line(polyline(...pts), w, color),
];
/** A thin outlined ring or line (hoops, handles); `o` is the ink showing on each side. */
const wire = (color: string, w: number, d: string, o = 1.2): Shape[] => [
  line(d, w + 2 * o),
  line(d, w, color),
];

/* ---------------------------------------------------------------------------------------------
 * Shared parts
 * ------------------------------------------------------------------------------------------- */

/** Every round face shares this head, so the faces read as one family. */
const HEAD = circle(16, 16, 13);
const face = (color: string, ...features: (Shape | Shape[])[]): IconDef => [
  fill(color, HEAD),
  ...features.flat(),
];

/** Bold dot eyes with a glint, mirrored around the middle. */
const dots = (y: number, dx = 4.8, rx = 1.7, ry = 2.3): Shape[] => [
  flat(INK, ellipse(16 - dx, y, rx, ry) + ellipse(16 + dx, y, rx, ry)),
  flat(
    P.white,
    circle(16 - dx + rx * 0.35, y - ry * 0.4, 0.55) + circle(16 + dx + rx * 0.35, y - ry * 0.4, 0.55),
  ),
];

/** Red-hot skin for the overheated face: redder than P.fire, hotter than P.red. */
const HOT = '#f2603a';
/** A tear stream down the left cheek, ending on the face's edge. */
const STREAM =
  'M7.6 16.2C8.8 15.6 10.4 15.6 11.6 16.2C11.4 20 11.4 24.2 11.6 28.2A13 13 0 0 1 7 25.4C7.2 22.4 7.2 19.4 7.6 16.2Z';
/** A steam puff blowing out of the left nostril. */
const PUFF: Ball[] = [
  [13.6, 19.8, 1.4],
  [11, 21.8, 2.1],
  [7.4, 24.4, 2.8],
];

/** The redneck's mullet: short at the sides, the party flaring out behind his neck. */
const MULLET =
  'M11.6 16.6C10 17.6 8.8 19.4 8.4 21.4C8 23 6.8 23.8 5.6 24.2C6.4 25.6 8.4 26.4 10.4 25.8C11.8 25.4 13.2 24.6 13.2 23.4L13 18.4Z';

/** Old Man Jenkins's fringe of white hair, left side. */
const OLD_HAIR: Ball[] = [
  [7.6, 8.4, 1.9],
  [5.8, 10.8, 2.1],
  [5.2, 13.6, 1.9],
];
/** Her teased bangs. */
const BANGS: Ball[] = [
  [9.6, 11.2, 2.2],
  [12, 9, 2.6],
  [15.2, 8.2, 2.8],
  [18.4, 8.8, 2.6],
  [20.6, 11, 2.1],
];
/** A ruffled ring of n puffs around (cx, cy). */
const ruffle = (cx: number, cy: number, R: number, r: number, n = 6): Ball[] =>
  Array.from({ length: n }, (_, i): Ball => {
    const a = (i * 2 * Math.PI) / n;
    return [cx + R * Math.cos(a), cy + R * Math.sin(a), r];
  });

/* ---------------------------------------------------------------------------------------------
 * The set
 * ------------------------------------------------------------------------------------------- */

export const people = {
  /** round yellow face, simple smile */
  smile: face(P.face, dots(13), line('M10.2 19.2Q16 25.2 21.8 19.2', 1.5)),
  /** round yellow face, flat line mouth, flat eyes */
  meh: face(
    P.face,
    flat(INK, rect(8.4, 12.6, 5.6, 2.2, 1.1) + rect(18, 12.6, 5.6, 2.2, 1.1)),
    line('M11 21H21', 1.5),
  ),
  /** round yellow face rolling its eyes up, flat unimpressed mouth */
  eyeroll: face(
    P.face,
    fill(P.white, ellipse(11.2, 13.4, 3.3, 3.8) + ellipse(20.8, 13.4, 3.3, 3.8), { w: 1.2 }),
    flat(INK, circle(11.6, 11.5, 1.8) + circle(21.2, 11.5, 1.8)),
    line('M11.6 21.6Q16 20.6 20.4 21.6', 1.5),
  ),
  /** round yellow face with X eyes and a wavy mouth */
  'knocked-out': face(
    P.face,
    line('M8.8 11.2L13.2 15.6M13.2 11.2L8.8 15.6M18.8 11.2L23.2 15.6M23.2 11.2L18.8 15.6', 1.6),
    line('M10 21.6Q11.9 19.4 13.8 21.6T17.6 21.6T21.4 21.6', 1.5),
  ),
  /** round yellow face with spiral eyes and a wobbly mouth, little stars */
  dizzy: face(
    P.face,
    line(spiral(11.2, 13.4, 1) + spiral(20.8, 13.4, -1), 1.3),
    line('M12.4 21.8Q14.2 19.8 16 21.8T19.6 21.8', 1.5),
    fill(P.gold, star(5.6, 7.2, 3) + star(26.4, 7.2, 3), { w: 1.2 }),
  ),
  /** round yellow face bawling: closed eyes, open mouth, two blue streams of tears */
  crying: face(
    P.face,
    fill(P.water, both(STREAM), { w: 1.2 }),
    line(both('M8.2 11.2L12.2 13.4L8.2 15.6'), 1.6),
    fill(INK, 'M12 24.6C12 20.8 13.8 19 16 19C18.2 19 20 20.8 20 24.6C18.6 23.8 13.4 23.8 12 24.6Z'),
  ),
  /** round yellow face, angry brows, two white steam puffs out of the nose */
  huffing: face(
    P.face,
    line(both('M8 9.4L13.4 11.6'), 1.5),
    dots(14.6, 4.6, 1.5, 1.9),
    line('M13.4 25.6Q16 23.4 18.6 25.6', 1.5),
    fill(P.white, bumps(PUFF) + bumps(mballs(PUFF))),
  ),
  /** round red-orange face, tongue out, sweat drops, droopy eyes */
  overheated: face(
    HOT,
    line(both('M8.4 10.8L13.2 9.4'), 1.5),
    flat(INK, both('M8.8 13.4L13.6 13.4A2.4 2.4 0 0 1 8.8 13.4Z')),
    fill(
      INK,
      'M11.4 19.6C13.2 18.8 18.8 18.8 20.6 19.6C20.6 22.4 18.6 24 16 24C13.4 24 11.4 22.4 11.4 19.6Z',
    ),
    fill(P.pink, 'M13.4 21.4L18.6 21.4L18.6 25C18.6 26.6 17.4 27.8 16 27.8C14.6 27.8 13.4 26.6 13.4 25Z', {
      w: 1.2,
    }),
    line('M16 22.6L16 25.4', 1.2),
    fill(P.water, drop(6.4, 9.4, 1.7) + drop(25.6, 8.2, 1.5), { w: 1.2 }),
  ),
  /** round yellow face with red hearts for eyes, big smile */
  'heart-eyes': face(
    P.face,
    fill(P.red, heart(10.8, 13, 7.6) + heart(21.2, 13, 7.6), { w: 1.2 }),
    fill(INK, 'M9.6 19C11.6 19.8 20.4 19.8 22.4 19C22.4 23.6 19.6 26.2 16 26.2C12.4 26.2 9.6 23.6 9.6 19Z'),
    flat(P.red, 'M12.6 24C14 22.8 18 22.8 19.4 24C18.6 24.9 17.4 25.4 16 25.4C14.6 25.4 13.4 24.9 12.6 24Z'),
  ),
  /** round yellow face smiling with closed happy eyes, pink cheeks, little hearts around */
  smitten: face(
    P.face,
    line(both('M8.6 14.4Q11 10.6 13.4 14.4'), 1.6),
    flat(P.blush, ellipse(8.4, 18.6, 2.2, 1.4) + ellipse(23.6, 18.6, 2.2, 1.4)),
    line('M11 19.8Q16 24.8 21 19.8', 1.5),
    fill(P.red, heart(5.8, 6.4, 6.6) + heart(26.7, 13.2, 5.2) + heart(5.3, 23.2, 4.8), { w: 1.2 }),
  ),
  /** round yellow face asleep, closed eyes, "z" letters */
  sleepy: face(
    P.face,
    line(both('M8.6 14Q11 16.8 13.4 14'), 1.6),
    fill(INK, ellipse(16, 21.8, 1.8, 2.2)),
    fill(P.sky, zee(21.8, 2.6, 6.6, 1.8) + zee(15.6, 7.6, 3.8, 1.4), { w: 1.2 }),
  ),
  /** round green face, puffed cheeks, about to be sick */
  queasy: face(
    P.lime,
    line(both('M8.6 11L13 9.8'), 1.5),
    dots(14.4, 4.6, 1.4, 1.8),
    flat(P.grass, ellipse(8.4, 20.6, 3, 2.4) + ellipse(23.6, 20.6, 3, 2.4)),
    line('M11.4 22Q12.9 20.4 14.4 22T17.4 22T20.4 22', 1.5),
    fill(P.water, drop(24.8, 8, 1.5), { w: 1.2 }),
  ),
  /** round yellow face grinning under a brown cowboy hat */
  cowboy: face(
    P.face,
    dots(17.6, 4.6, 1.6, 2.1),
    fill(
      INK,
      'M10.4 21.4C12.4 22.2 19.6 22.2 21.6 21.4C21.4 25 19.2 27.2 16 27.2C12.8 27.2 10.6 25 10.4 21.4Z',
    ),
    flat(P.white, 'M11.8 22.8C13.8 23.3 18.2 23.3 20.2 22.8L20 24C18 24.5 14 24.5 12 24Z'),
    fill(
      P.wood,
      'M5.6 10.4C5 6.2 7 2.9 10.6 2.8C12.8 2.7 14 4 16 4C18 4 19.2 2.7 21.4 2.8C25 2.9 27 6.2 26.4 10.4Z',
    ),
    flat(P.darkBrown, rect(6.4, 8, 19.2, 2.4)),
    fill(
      P.wood,
      'M2.8 6.8C4.6 9.6 9 11 16 11C23 11 27.4 9.6 29.2 6.8C29.6 10.8 25.6 14.4 16 14.4C6.4 14.4 2.4 10.8 2.8 6.8Z',
    ),
  ),
  /** round yellow face with a white bandage wrapped around the head */
  hurt: face(
    P.face,
    dots(17.4, 4.6, 1.5, 1.9),
    line('M12.4 23.8Q16 21 19.6 23.8', 1.5),
    fill(
      P.white,
      'M3.4 10.8Q13 5.6 23.6 4.6A13.2 13.2 0 0 1 27.6 9.4Q16 10.6 2.8 15.8A13.2 13.2 0 0 1 3.4 10.8Z',
    ),
    line('M8 9.6L9.4 13.2M12.4 7.8L13.6 11.8M17 6.6L17.8 10.6', 1.2, P.lightGray),
  ),
  /** a green alien head with big black almond eyes */
  alien: [
    fill(
      P.lime,
      'M16 29C11.6 29 4.6 22.4 4.6 13.8C4.6 7.2 9.8 3 16 3C22.2 3 27.4 7.2 27.4 13.8C27.4 22.4 20.4 29 16 29Z',
    ),
    flat(INK, both('M6.8 13.4C9.8 12.4 14.4 14.8 14.6 19.4C11.4 20 7 17.6 6.8 13.4Z')),
    flat(P.white, ellipse(9.6, 14.6, 1, 0.7) + ellipse(22.4, 14.6, 1, 0.7)),
    line('M14 24.4Q16 25.4 18 24.4', 1.3),
  ],
  /** a white cartoon ghost with a wavy bottom */
  ghost: [
    fill(
      P.white,
      'M5.8 25.6L5.8 14C5.8 7.6 10.4 3 16 3C21.6 3 26.2 7.6 26.2 14L26.2 25.6C26.2 28.2 24.4 29.2 22.8 29.2C21.2 29.2 19.8 28.2 19.4 26.6C19 28.2 17.6 29.2 16 29.2C14.4 29.2 13 28.2 12.6 26.6C12.2 28.2 10.8 29.2 9.2 29.2C7.6 29.2 5.8 28.2 5.8 25.6Z',
    ),
    flat(INK, ellipse(12.4, 13.4, 1.8, 2.6) + ellipse(19.6, 13.4, 1.8, 2.6)),
    fill(INK, ellipse(16, 20.4, 2, 2.6)),
    flat(P.pink, ellipse(9.4, 17.6, 1.6, 1) + ellipse(22.6, 17.6, 1.6, 1), 0.8),
  ],
  /** a white cartoon skull */
  skull: [
    fill(
      P.white,
      'M16 3C9.2 3 4.6 7.8 4.6 14.2C4.6 17.8 6.2 20.6 8.8 22L8.8 25.8C8.8 27.4 9.8 28.4 11.4 28.4L20.6 28.4C22.2 28.4 23.2 27.4 23.2 25.8L23.2 22C25.8 20.6 27.4 17.8 27.4 14.2C27.4 7.8 22.8 3 16 3Z',
    ),
    flat(INK, ellipse(11.2, 14.8, 3.1, 3.4) + ellipse(20.8, 14.8, 3.1, 3.4)),
    flat(INK, poly(16, 18.8, 17.8, 21.8, 14.2, 21.8)),
    line('M12.8 24.4L12.8 28.2M16 24.4L16 28.2M19.2 24.4L19.2 28.2', 1.2),
  ],
  /** a friendly redneck guy, head and shoulders: brown mullet, bushy mustache, red trucker cap, tank top. Used on the "Man" button in the character creator */
  man: [
    fill(P.skin, 'M13 18L13 24.2C8.4 24.6 4 26.6 4 30L28 30C28 26.6 23.6 24.6 19 24.2L19 18Z'),
    fill(
      P.white,
      'M9 30L10 25.6C10.2 24.8 10.8 24.4 11.6 24.6C12.4 26.4 14 27.4 16 27.4C18 27.4 19.6 26.4 20.4 24.6C21.2 24.4 21.8 24.8 22 25.6L23 30Z',
    ),
    fill(P.brown, both(MULLET)),
    fill(P.skin, ellipse(9.4, 14.8, 1.7, 2.3) + ellipse(22.6, 14.8, 1.7, 2.3)),
    fill(
      P.skin,
      'M9.6 11.6C9.6 7 12.4 4.8 16 4.8C19.6 4.8 22.4 7 22.4 11.6L22.4 15.4C22.4 20.2 19.6 23.4 16 23.4C12.4 23.4 9.6 20.2 9.6 15.4Z',
    ),
    fill(P.red, 'M8.8 11.2C8.6 5.8 11.6 2.6 16 2.6C20.4 2.6 23.4 5.8 23.2 11.2Z'),
    flat(P.white, rect(13.4, 5.6, 5.2, 3.4, 1)),
    fill(
      P.darkRed,
      'M7 11.2C9.6 9.8 22.4 9.8 25 11.2C25.8 12.6 25 13.6 23.4 13.4C19.4 12.8 12.6 12.8 8.6 13.4C7 13.6 6.2 12.6 7 11.2Z',
    ),
    flat(INK, ellipse(13, 15.2, 1.2, 1.5) + ellipse(19, 15.2, 1.2, 1.5)),
    fill(
      P.brown,
      'M10.4 20C10.6 17.8 13.6 16.8 16 18C18.4 16.8 21.4 17.8 21.6 20C19.8 20.9 17.6 20.6 16 19.6C14.4 20.6 12.2 20.9 10.4 20Z',
      { w: 1.2 },
    ),
    line('M14.2 21.5Q16 22.3 17.8 21.5', 1.2),
  ],
  /** a friendly southern woman, head and shoulders: big teased/ponytail blonde hair with a pink scrunchie, gold hoop earrings, lipstick. Used on the "Woman" button in the creator */
  woman: [
    fill(
      P.gold,
      'M20.6 6.8C21.6 3.6 24.8 2.2 27.4 3.2C29.4 4.2 29.4 8 28.8 11.6C28.2 14.6 26.8 17.6 24.6 19.4C25.4 16.6 25.8 13.6 25.2 11.2C24.6 9 23 7.4 20.6 6.8Z',
    ),
    fill(
      P.gold,
      'M6.6 18.6C4.4 15.8 4 11.4 5.6 8.2C7.2 4.8 10.8 2.6 14.6 2.6C18.4 2.6 21.8 4.6 23 7.8C24 10.6 23.6 14.6 22.2 17.6Z',
    ),
    fill(P.skin, 'M12 18L12 22.6C7.4 23 3.4 25.6 3.4 30L27 30C27 25.6 22.6 23 18 22.6L18 18Z'),
    fill(
      P.teal,
      'M6.2 30C6.4 27.2 7.4 25.4 9 24.4C10.6 26.4 12.6 27.2 15 27.2C17.4 27.2 19.4 26.4 21 24.4C22.6 25.4 23.6 27.2 23.8 30Z',
    ),
    ...wire(P.gold, 1.2, circle(9.2, 20, 1.9) + circle(20.8, 20, 1.9), 0.7),
    fill(
      P.skin,
      'M8.8 13.6C8.8 9.2 11.6 7 15 7C18.4 7 21.2 9.2 21.2 13.6L21.2 15.2C21.2 20 18.4 23.2 15 23.2C11.6 23.2 8.8 20 8.8 15.2Z',
    ),
    fill(P.gold, bumps(BANGS)),
    fill(P.hotPink, bumps(ruffle(22.2, 5.4, 1.5, 1.15), true), { w: 1.2 }),
    flat(INK, ellipse(12.4, 14.8, 1.2, 1.6) + ellipse(17.6, 14.8, 1.2, 1.6)),
    line('M11.2 13.6L10 12.6M18.8 13.6L20 12.6', 1.2),
    flat(P.blush, ellipse(10.8, 17.6, 1.5, 0.9) + ellipse(19.2, 17.6, 1.5, 0.9)),
    flat(
      P.hotPink,
      'M12.4 19.2C13.2 18.2 14.2 18.1 15 18.7C15.8 18.1 16.8 18.2 17.6 19.2C17 20.6 16 21.3 15 21.3C14 21.3 13 20.6 12.4 19.2Z',
    ),
    line('M12.9 19.4Q15 20.2 17.1 19.4', 0.9),
  ],
  /** an old man face: bald on top, white beard, glasses (Old Man Jenkins) */
  'old-man': [
    fill(P.white, bumps(OLD_HAIR) + bumps(mballs(OLD_HAIR))),
    fill(P.skin, ellipse(5.8, 16.4, 2, 2.6) + ellipse(26.2, 16.4, 2, 2.6)),
    fill(
      P.skin,
      'M6 14.4C6 7.8 10.4 3.6 16 3.6C21.6 3.6 26 7.8 26 14.4L26 17C26 23.4 21.6 28 16 28C10.4 28 6 23.4 6 17Z',
    ),
    shine(ellipse(12.4, 7, 3, 1.4), 0.55),
    fill(
      P.white,
      'M6.2 17C7 20 9.4 21.6 12 21.4C13.6 21.2 14.8 20.4 16 20.4C17.2 20.4 18.4 21.2 20 21.4C22.6 21.6 25 20 25.8 17C26.8 23.8 22.4 29.6 16 29.6C9.6 29.6 5.2 23.8 6.2 17Z',
    ),
    fill(P.glass, circle(11.4, 14.2, 3.2) + circle(20.6, 14.2, 3.2), { w: 1.4 }),
    line('M14.6 13.6Q16 12.8 17.4 13.6', 1.4),
    flat(INK, circle(11.4, 14.4, 1.1) + circle(20.6, 14.4, 1.1)),
    fill(P.white, both('M8 10.4C9 8.6 12.4 8.2 14.4 9.6C12.8 10.6 10.2 10.9 8 10.4Z'), { w: 1.2 }),
    fill(P.skin, ellipse(16, 18.4, 2, 1.8), { w: 1.2 }),
    fill(
      P.white,
      'M10.8 21.2C11.8 19.2 14.6 19 16 20.2C17.4 19 20.2 19.2 21.2 21.2C19.6 22.2 17.6 22 16 21.2C14.4 22 12.4 22.2 10.8 21.2Z',
      { w: 1.2 },
    ),
    line('M14.4 23.4Q16 24.2 17.6 23.4', 1.2),
  ],
  /** a baby face with a tuft of hair and a pacifier */
  baby: [
    fill(P.skin, circle(5.2, 17.6, 2.2) + circle(26.8, 17.6, 2.2)),
    fill(P.skin, circle(16, 17, 12)),
    fill(
      P.wood,
      'M13.4 6.4C13.8 3.6 16.4 2.2 18.8 3C20.6 3.6 21 5.6 19.6 6.4C19.8 5 18.6 4.4 17.4 4.8C16.2 5.2 16 6.4 16.6 7.2C15.6 7.4 14.4 7.2 13.4 6.4Z',
      { w: 1.2 },
    ),
    ...dots(15.8, 4.8, 1.6, 2),
    flat(P.blush, ellipse(8.6, 20.4, 2, 1.3) + ellipse(23.4, 20.4, 2, 1.3)),
    ...wire(P.hotPink, 1.4, circle(16, 25.4, 2)),
    fill(
      P.sky,
      'M11 22.4C11 20.4 13.6 20.2 16 21C18.4 20.2 21 20.4 21 22.4C21 24.4 18.4 24.6 16 23.8C13.6 24.6 11 24.4 11 22.4Z',
    ),
    fill(P.white, circle(16, 22.4, 1.3), { w: 1.2 }),
  ],
  /** a plain gray head-and-shoulders silhouette (generic avatar) */
  person: [
    fill(P.gray, 'M4.6 29.2C4.6 22.8 9.4 19 16 19C22.6 19 27.4 22.8 27.4 29.2Z'),
    fill(P.gray, circle(16, 10.6, 6.6)),
  ],
  /** a person (head and shoulders) raising one hand high, like "me! I can help" */
  'raise-hand': [
    ...limb(P.skin, 3, 22.4, 24, 24.6, 10.2),
    ...shift(0, 0.8, [
      fill(P.skin, pill(22.6, 9.4, 20, 6.6, 1.1)),
      fill(
        P.skin,
        'M21.6 11.4L21.6 4.2A1 1 0 0 1 23.6 4.2L23.6 6L23.6 3.2A1 1 0 0 1 25.6 3.2L25.6 6L25.6 4.2A1 1 0 0 1 27.6 4.2L27.6 9.6C27.6 10.6 26.8 11.4 25.8 11.4Z',
      ),
    ]),
    fill(P.blue, 'M3.4 30C3.4 25.4 7 22.6 11.6 22.4L15.4 22.4C19.8 22.6 23 24.8 24 28L24.4 30Z'),
    ...limb(P.blue, 3.6, 21.4, 26.4, 22.4, 21.6),
    fill(P.skin, circle(13.2, 14.6, 6.4)),
    fill(
      P.brown,
      'M6.8 14.4C6.2 9.8 9.2 7.6 13.4 7.6C17.4 7.6 20.2 10 19.6 14.2C18.4 12 16.2 10.8 13.6 11.2C12 11.4 11.6 12.6 10.2 13C9 13.4 7.8 13.6 6.8 14.4Z',
    ),
    flat(INK, ellipse(11, 15.4, 1.1, 1.4) + ellipse(15.6, 15.4, 1.1, 1.4)),
    fill(
      INK,
      'M11.6 17.8C12.6 18.3 14.2 18.3 15.2 17.8C15 19.2 14.2 19.9 13.4 19.9C12.6 19.9 11.8 19.2 11.6 17.8Z',
      { w: 1 },
    ),
  ],
  /** a stick-ish cartoon person doing a cartwheel/backflip with motion arcs */
  cartwheel: cartwheel(),
  /** a cartoon person running fast, motion lines */
  runner: shift(0.8, -0.4, [
    line('M2.6 9.4L7.6 9.4M3.4 13.4L8.2 13.4M2.6 17.4L6 17.4', 1.5, P.gray),
    ...limb(P.skin, 2.2, 19.4, 10.2, 15.6, 13.6, 12.2, 12),
    ...limb(P.skin, 2.4, 15, 17.6, 11.4, 22.2, 6.4, 21.4),
    ...limb(P.red, 4.4, 19.4, 10.2, 15, 17.6),
    ...limb(P.blue, 3.6, 15, 17.6, 17, 19.2),
    ...limb(P.skin, 2.4, 16.6, 19, 20.4, 21, 19.2, 27.4),
    ...limb(P.skin, 2.2, 19.4, 10.2, 22.4, 13.8, 25.6, 11.2),
    fill(P.skin, circle(21.2, 6.4, 3)),
  ]),
  /** two cartoon wrestlers grappling (singlets, one red one blue) */
  wrestlers: shift(0, -2.2, [
    ...limb(P.skin, 2.4, 9.4, 19.6, 6.6, 23.6, 5, 28.6),
    ...limb(P.skin, 2.4, 9.4, 19.6, 12, 24, 11.6, 28.6),
    ...limb(P.skin, 2.4, 22.6, 19.6, 25.4, 23.6, 27, 28.6),
    ...limb(P.skin, 2.4, 22.6, 19.6, 20, 24, 20.4, 28.6),
    ...limb(P.red, 4.8, 13.2, 12.8, 9.4, 19.6),
    ...limb(P.blue, 4.8, 18.8, 12.8, 22.6, 19.6),
    ...limb(P.skin, 2.2, 13.2, 13.2, 16.6, 16.4, 19.4, 14.4),
    ...limb(P.skin, 2.2, 18.8, 13.2, 15.4, 16.4, 12.6, 14.4),
    fill(P.skin, circle(12.4, 9, 3.1) + circle(19.6, 9, 3.1)),
  ]),
  /** a county man in a short-sleeve white dress shirt, clip-on tie and dark sunglasses */
  revenuer: [
    fill(P.skin, 'M12.8 18L12.8 22.6C8 23 3.8 25.6 3.8 30L28.2 30C28.2 25.6 24 23 19.2 22.6L19.2 18Z'),
    fill(P.white, 'M3.6 30C3.6 25.6 7.2 23.2 11.6 22.8L16 26.4L20.4 22.8C24.8 23.2 28.4 25.6 28.4 30Z'),
    fill(
      P.white,
      poly(12.2, 21.8, 16, 24.6, 13.6, 26.8, 11, 24.6) + poly(19.8, 21.8, 16, 24.6, 18.4, 26.8, 21, 24.6),
      { w: 1.2 },
    ),
    ...limb(P.blue, 1, 21.8, 26.4, 21.8, 23.8),
    ...limb(P.red, 1, 23.6, 26.4, 23.6, 24.4),
    fill(P.white, rect(20.4, 25.8, 4.4, 3.8, 0.6), { w: 1.2 }),
    fill(P.darkRed, poly(15.2, 25.2, 16.8, 25.2, 17.8, 29, 16, 30.2, 14.2, 29), { w: 1.2 }),
    fill(P.darkRed, poly(14.6, 23.8, 17.4, 23.8, 16.9, 25.6, 15.1, 25.6), { w: 1.2 }),
    fill(P.skin, ellipse(9.4, 14.8, 1.7, 2.3) + ellipse(22.6, 14.8, 1.7, 2.3)),
    fill(
      P.skin,
      'M9.6 12.4C9.6 7.8 12.4 5.4 16 5.4C19.6 5.4 22.4 7.8 22.4 12.4L22.4 15C22.4 19.4 19.6 22.2 16 22.2C12.4 22.2 9.6 19.4 9.6 15Z',
    ),
    fill(
      P.darkBrown,
      'M9.4 12.2C9 7.4 11.8 4.4 16.2 4.4C20.4 4.4 23 7 22.6 12C22.2 10.2 21.4 9 20 8.4C17 7.4 13 8.4 11 9.4C10.2 10.2 9.8 11 9.4 12.2Z',
    ),
    fill(
      P.black,
      'M9.2 12.6L22.8 12.6L22.8 13.6C22.8 15.8 21.8 16.8 19.8 16.8C18.2 16.8 17 16 16.7 14.4L15.3 14.4C15 16 13.8 16.8 12.2 16.8C10.2 16.8 9.2 15.8 9.2 13.6Z',
    ),
    shine('M10.6 13.6L12.4 13.6L11.2 15.4Z', 0.5),
    line('M14 19.4L18 19.4', 1.4),
  ],
  /** a garden gnome: red pointy hat, white beard, blue coat (part of a "gnome militia", so a tiny stick rifle is fine) */
  gnome: shift(-2, 0, [
    fill(P.blue, 'M8.4 27.6C8.4 22 10.2 18.6 12.8 17.4L19.2 17.4C21.8 18.6 23.6 22 23.6 27.6Z'),
    flat(P.black, rect(9.4, 23.4, 13.2, 2)),
    flat(P.gold, rect(14.6, 23.1, 2.8, 2.6, 0.5)),
    fill(P.darkBrown, ellipse(12.2, 27.8, 3.4, 1.8) + ellipse(19.8, 27.8, 3.4, 1.8)),
    fill(
      P.white,
      'M9.6 13.8C9.4 18.4 12.2 22 16 23.4C19.8 22 22.6 18.4 22.4 13.8C20.6 15.6 18.4 16.2 16 16.2C13.6 16.2 11.4 15.6 9.6 13.8Z',
    ),
    fill(P.skin, ellipse(16, 13.4, 5.6, 3.4)),
    flat(INK, ellipse(13.6, 12.8, 0.9, 1.1) + ellipse(18.4, 12.8, 0.9, 1.1)),
    fill(P.blush, circle(16, 14.6, 1.9), { w: 1.2 }),
    fill(
      P.white,
      'M11.6 16.2C12.8 14.8 14.8 14.8 16 15.8C17.2 14.8 19.2 14.8 20.4 16.2C19 17.2 17.4 17.2 16 16.6C14.6 17.2 13 17.2 11.6 16.2Z',
      { w: 1.2 },
    ),
    fill(
      P.red,
      'M8 12.2C9.2 8.4 11.8 4.8 15.2 3C16.6 2.3 18 2.8 18.4 4C20.6 6.6 23 9.2 24 12.2C19.2 13.4 12.8 13.4 8 12.2Z',
    ),
    ...limb(P.charcoal, 1.2, 23.4, 20.6, 27, 10.4),
    fill(P.wood, poly(19.8, 27.4, 22.4, 28.2, 24.6, 21.6, 22.6, 20.6)),
    fill(P.skin, circle(23.4, 20.6, 1.7), { w: 1.2 }),
  ]),
  /** a woman's head with big hair and a pair of scissors snipping beside it (a salon) */
  haircut: [
    fill(
      P.rust,
      bumps(
        [
          [5.8, 18.4, 2.6],
          [5.8, 13.6, 2.8],
          [6.8, 8.8, 3],
          [10.4, 6, 3.1],
          [14.8, 5.8, 3],
          [18.4, 8.2, 2.9],
          [19.6, 12.4, 2.7],
          [19, 16.8, 2.5],
          [12.2, 15, 6.6],
        ],
        true,
      ),
    ),
    fill(P.lavender, 'M3.4 30C3.6 26 6.6 23.4 10 22.8L15.6 22.8C19 23.4 22 26 22.2 30Z'),
    fill(P.skin, ellipse(12.4, 15.6, 5, 5.8)),
    fill(
      P.rust,
      bumps([
        [8, 12.2, 2],
        [10.6, 10.2, 2.2],
        [13.6, 9.8, 2.3],
        [16.2, 11.2, 2],
      ]),
    ),
    flat(INK, ellipse(10.4, 15.6, 1, 1.3) + ellipse(14.4, 15.6, 1, 1.3)),
    flat(P.blush, ellipse(9.2, 18, 1.2, 0.8) + ellipse(15.6, 18, 1.2, 0.8)),
    line('M11 18.6Q12.4 19.8 13.8 18.6', 1.2, P.darkRed),
    ...wire(P.gray, 1.4, polyline(21.4, 10.6, 25.4, 7.4) + polyline(21.4, 10.6, 25.2, 14)),
    ...wire(P.teal, 1.6, circle(26.2, 6.4, 1.9) + circle(26, 14.8, 1.9)),
    fill(P.silver, blade(21.4, 10.6, 13.4, 5.8, 1.5) + blade(21.4, 10.6, 13.8, 14.8, 1.5), { w: 1.2 }),
    fill(P.gray, circle(21.4, 10.6, 0.9), { w: 1 }),
  ],
  /** a dark silhouette head in profile with sound lines coming from the mouth (talking trash) */
  shout: [
    fill(
      P.darkGray,
      'M3.6 29.6L3.6 25.4C3.6 23.6 4.4 22.2 5.6 21.2C4.2 19.4 3.4 17 3.4 14.4C3.4 8.4 8 3.6 14 3.6C19 3.6 22.4 6.8 22.4 11.4C22.4 12.6 23.2 13.8 24 15C24.4 15.6 24 16.2 23.2 16.4L22.4 16.6L22.6 17.6L19.6 19L22.6 20C22.4 21.8 21.6 22.6 19.6 22.6L17 22.4C16.6 23.6 16.8 25 17.6 26L18.6 29.6Z',
    ),
    ...limb(P.orange, 1.4, 25, 15.2, 27.4, 13.6),
    ...limb(P.orange, 1.4, 25.4, 19.2, 27.7, 19.2),
    ...limb(P.orange, 1.4, 25, 23.2, 27.4, 24.8),
  ],
  /** a single eye (white, blue iris) with lashes */
  eye: shift(0, 1.6, [
    fill(P.white, 'M2.8 16C7.4 9 24.6 9 29.2 16C24.6 23 7.4 23 2.8 16Z'),
    fill(P.blue, circle(16, 16, 5.2), { w: 1.2 }),
    flat(INK, circle(16, 16, 2.4)),
    flat(P.white, circle(17.8, 14.2, 1.1)),
    line('M2.8 16C7.4 9 24.6 9 29.2 16C24.6 23 7.4 23 2.8 16Z', 1.6),
    line('M8.2 12L6.4 9.6M12 11.1L11.2 8.2M16 10.8L16 7.6M20 11.1L20.8 8.2M23.8 12L25.6 9.6', 1.5),
  ]),
  /** a pair of eyes glancing to the side */
  eyes: [
    fill(P.white, ellipse(10, 16, 5.6, 8.2) + ellipse(22, 16, 5.6, 8.2)),
    flat(INK, ellipse(7.8, 17, 2.6, 3.6) + ellipse(19.8, 17, 2.6, 3.6)),
    flat(P.white, circle(8.6, 15.4, 0.9) + circle(20.6, 15.4, 0.9)),
  ],
  /** a flexed arm showing a bicep (skin tone, a little tattoo is fine) */
  flex: [
    fill(
      P.skin,
      'M3.4 18C5.4 13.4 9.8 10.8 13.8 11.6C17 12.2 19.4 14.6 20 17.4C20.6 15.4 20.8 13.2 20.6 11.4L26.4 11C27.2 14.6 27.8 18.4 27.6 21.6C27.4 25.6 25.2 27.8 21.6 27.8L3.4 27.8Z',
    ),
    fill(
      P.skin,
      'M21.4 3C25.4 2.8 28.6 4.8 28.6 8C28.6 11.2 26.4 13 23.4 13L21 13A1.7 1.7 0 0 1 20.4 9.8A1.7 1.7 0 0 1 19.8 6.6A2 2 0 0 1 21.4 3Z',
    ),
    line('M20.4 9.8L22.8 9.8M19.8 6.6L22.4 6.6', 1.2),
    line('M20 17.4C19.8 19.6 18.8 21 17.2 21.8', 1.2),
    shine(ellipse(11.6, 14.6, 3, 1.2), 0.45),
    fill(P.red, heart(10.6, 20, 4.6), { w: 1 }),
    fill(P.white, 'M3 15.8C5.2 15.2 7.4 16.2 8.2 18.4C9.2 21 9.2 26 8 28.6C6.4 29.2 4.4 29.2 3 28.6Z'),
  ],
  /** two hands pressed together praying */
  pray: [
    fill(
      P.skin,
      both(
        'M16 3.2C14.6 3.2 13.6 4.2 13 6L10.6 14.4C10 16.4 9.2 18.6 9.4 21C9.6 23 10.4 24.4 11.4 25.4L16 25.4Z',
      ),
    ),
    line(both('M14.6 5.6L12.2 14.4'), 1.2),
    fill(P.skin, pill(14.6, 22.6, 15.2, 15.6, 1.4) + pill(17.4, 22.6, 16.8, 15.6, 1.4), { w: 1.2 }),
    fill(P.blue, both('M10.4 24.6L16 24.6L16 29.6L9.6 29.6Z')),
  ],
  /** a fist punching forward (a dap) */
  'fist-bump': shift(0, -1.8, [
    fill(P.blue, rect(10.4, 24, 11.6, 5.6, 1.4)),
    fill(
      P.skin,
      'M5.8 12C5.8 8.6 8 6.6 10.8 6.6L21.4 6.6C24.4 6.6 26.4 8.6 26.4 11.8L26.4 18.2C26.4 22.4 23.4 25.6 19.2 25.6L13 25.6C8.8 25.6 5.8 22.4 5.8 18.2Z',
    ),
    line('M11.2 7L11.2 13.6M16.2 6.8L16.2 13.6M21.2 7L21.2 13.6', 1.2),
    fill(
      P.skin,
      'M5.8 14.8C7.6 13.6 12 13.4 16.2 14.4C18.2 14.8 18.6 17.6 16.6 18.2C13 19.2 9 18.8 6.2 17.6Z',
      { w: 1.4 },
    ),
  ]),
  /** two hands shaking */
  handshake: [
    fill(
      P.skin,
      stretch(
        'M7.6 11.6C10.4 10.6 13.2 10.6 15.6 11.4L21.6 13.8C23 14.4 23.2 16.4 22 17.2L18.8 19.4L8.8 20.6Z',
        1.4,
        15.8,
      ),
    ),
    fill(
      P.skin,
      stretch(
        'M24.4 11C21 10.4 17.4 11.2 14.6 12.8L10.4 15.6C9.4 16.4 9.8 18 11 18L12.8 18C12.8 19.4 14 20.4 15.4 20.2L17.6 19.8C18.2 20.8 19.6 21.2 20.8 20.6L23.6 19.6Z',
        1.4,
        15.8,
      ),
    ),
    line(stretch('M12.8 18L14.2 16.4M15.4 20.2L16.4 17.6M17.6 19.8L18.4 17.8', 1.4, 15.8), 1.2),
    fill(P.skin, pill(6.4, 11.2, 19.4, 12.4, 1.7), { w: 1.4 }),
    fill(P.blue, stretch(poly(3, 12, 7.8, 10.8, 9.4, 20.2, 4.6, 21.6), 1.4, 15.8)),
    fill(P.orange, stretch(poly(29, 12, 24.2, 10.8, 22.6, 20.2, 27.4, 21.6), 1.4, 15.8)),
  ],
  /** a hand giving a thumbs up */
  'thumbs-up': shift(0.8, 0, [
    fill(
      P.skin,
      'M9.6 17.6C9.6 16 10.4 15 11.6 14.6C11.2 11 11.8 7.4 13 5C14 3 17.4 3.4 17.2 6C17 8.6 16.4 11.6 16.8 14.4L23.6 14.4C25.4 14.4 26.6 15.6 26.6 17.4L26.6 25C26.6 26.8 25.2 28.2 23.4 28.2L12.4 28.2C10.8 28.2 9.6 27 9.6 25.4Z',
    ),
    line('M17.6 17.8L26.4 17.8M17.6 21.2L26.6 21.2M17.6 24.6L26.4 24.6', 1.2),
    fill(P.blue, rect(3.6, 14.8, 6.8, 14.4, 1.4)),
  ]),
  /** a hand making the "call me"/shaka sign (thumb and pinky out) */
  shaka: shift(0, -2.8, [
    fill(P.skin, pill(12.4, 18, 5.2, 11.4, 2.3)),
    fill(P.skin, pill(20, 17.8, 27.4, 13.8, 1.9)),
    fill(P.skin, rect(10.4, 13.2, 12, 13, 4.6)),
    fill(
      P.skin,
      bumps([
        [12.8, 14.4, 1.9],
        [15.8, 13.6, 2],
        [18.8, 14, 1.9],
      ]),
      { w: 1.4 },
    ),
    fill(P.blue, rect(11.2, 25.2, 10.4, 4.6, 1.2)),
  ]),
  /** an open raised hand, palm out (high five / "up top") */
  'high-five': shift(0, 0.4, [
    fill(P.skin, pill(10.4, 21.4, 4.8, 14.6, 2)),
    fill(
      P.skin,
      'M8.6 16C8.6 14.4 9.8 13.6 11.4 13.6L21.6 13.6C23.2 13.6 24.2 14.6 24.2 16.2L24.2 22C24.2 26 21.4 28.6 17.2 28.6L15.4 28.6C11.4 28.6 8.6 25.8 8.6 21.8Z',
    ),
    fill(
      P.skin,
      pill(11, 16, 10.4, 6.4, 1.8) +
        pill(14.8, 16, 14.8, 4.6, 1.8) +
        pill(18.6, 16, 19, 5.6, 1.8) +
        pill(22, 16.4, 23.2, 9, 1.7),
    ),
    flat(P.skin, rect(10.2, 13.6, 13, 5.4)),
    line('M26.4 5L28.2 3.6M27.4 8.8L29.2 8.4M5.4 8.4L3.6 7', 1.5),
  ]),
  /** a hand with the index finger pointing up */
  'point-up': shift(-1, 0, [
    fill(
      P.skin,
      'M11.4 17L11.4 6.4C11.4 4.8 12.6 3.6 14 3.6C15.4 3.6 16.6 4.8 16.6 6.4L16.6 13.4L21.4 13.4C23.2 13.4 24.6 14.8 24.6 16.6L24.6 24.4C24.6 26.6 22.8 28.4 20.6 28.4L14 28.4C11.4 28.4 9.4 26.4 9.4 23.8L9.4 19C9.4 17.8 10.2 17.2 11.4 17Z',
    ),
    line('M16.6 17L24.6 17M16.6 20.6L24.6 20.6', 1.2),
    fill(
      P.skin,
      'M9.6 20.4C11.4 19.4 15 19.4 17.4 20.2C18.8 20.6 18.8 22.6 17.4 23C15 23.8 11.6 23.6 9.6 22.6Z',
      { w: 1.2 },
    ),
    fill(P.blue, rect(10.4, 27, 13.2, 3, 1)),
  ]),
  /** a hand showing off long hot-pink painted nails, a little sparkle */
  nails: shift(-1.6, 0, [
    fill(
      P.skin,
      'M8.6 18C8.6 16.4 9.8 15.6 11.4 15.6L21.6 15.6C23.2 15.6 24.2 16.6 24.2 18.2L24.2 23C24.2 27 21.4 29.6 17.2 29.6L15.4 29.6C11.4 29.6 8.6 26.8 8.6 22.8Z',
    ),
    fill(
      P.skin,
      pill(11, 18, 10.4, 8.4, 1.8) +
        pill(14.8, 18, 14.8, 6.6, 1.8) +
        pill(18.6, 18, 19, 7.6, 1.8) +
        pill(22, 18.4, 23.2, 11, 1.7),
    ),
    flat(P.skin, rect(10.2, 15.6, 13, 5.6)),
    fill(
      P.hotPink,
      nail(10.4, 8.2, 10.1, 4.2, 1.5) +
        nail(14.8, 6.4, 14.8, 2.8, 1.5) +
        nail(19, 7.4, 19.3, 3.6, 1.5) +
        nail(23.1, 10.8, 23.9, 7.2, 1.4),
      { w: 1.2 },
    ),
    fill(P.gold, sparkle(28, 5.2, 2.6), { w: 1.1 }),
  ]),
  /** red lipstick lips (a kiss mark) */
  lips: shift(0, -1, [
    fill(
      P.red,
      'M2.8 16C5.6 11.4 9.4 8.6 12.6 9.4C14 9.8 15 10.6 16 11.6C17 10.6 18 9.8 19.4 9.4C22.6 8.6 26.4 11.4 29.2 16C26 22 21.2 25 16 25C10.8 25 6 22 2.8 16Z',
    ),
    line('M3.4 16C7.4 15.6 11.4 16.8 16 16.8C20.6 16.8 24.6 15.6 28.6 16', 1.4),
    shine(ellipse(19.6, 20.6, 2.6, 1), 0.45),
  ]),
  /** a set of false teeth (dentures): pink gums, white teeth */
  dentures: [
    fill(P.white, teeth(5.8, 15.6, 20.4, 6, 6.6), { w: 1.2 }),
    fill(P.white, teeth(5.4, 9.4, 21.2, 6, 7), { w: 1.2 }),
    fill(P.pink, 'M4 12C3.4 7.6 9 5 16 5C23 5 28.6 7.6 28 12C24 11 20 10.6 16 10.6C12 10.6 8 11 4 12Z'),
    fill(
      P.pink,
      'M4.4 20.2C8.4 21.2 12.2 21.6 16 21.6C19.8 21.6 23.6 21.2 27.6 20.2C28.2 24.6 22.8 27 16 27C9.2 27 3.8 24.6 4.4 20.2Z',
    ),
    shine(ellipse(10.4, 7.6, 2.6, 1), 0.5),
  ],
  /** a pair of pink lungs */
  lungs: [
    fill(
      P.pink,
      both(
        'M13.8 9.2C10 8.6 5.4 12.6 4.6 19.4C4 24.6 5.6 28.4 9 28.4C11.6 28.4 13.6 26.8 14.2 24.4C14.6 21 14.4 14 13.8 9.2Z',
      ),
    ),
    ...limb(P.cream, 2.2, 16, 5.2, 16, 12.4),
    ...limb(P.cream, 1.6, 16, 12, 12.4, 15.6, 11.4, 19),
    ...limb(P.cream, 1.6, 16, 12, 19.6, 15.6, 20.6, 19),
  ],
  /** a hand holding a pen, signing on paper */
  writing: [
    fill(P.white, poly(2.8, 15.4, 20.4, 12.6, 22.6, 27, 5, 29.6)),
    line(
      'M6.4 25.8C7.6 22.4 9 22.2 9.2 24.4C9.4 26.6 11 23.4 12.2 22.8C13.2 22.4 13 24.8 14.2 24.6',
      1.4,
      P.blue,
    ),
    ...limb(P.charcoal, 1, 15.4, 21.2, 14.2, 23),
    ...limb(P.blue, 2.4, 15.4, 21.2, 25.8, 5.4),
    fill(P.teal, poly(24.2, 6.6, 29, 9.4, 27, 14, 22.2, 11.2)),
    fill(
      P.skin,
      'M17.6 13.4C18.4 10.4 21.6 8.8 24.6 9.6C27.4 10.4 28.8 13.2 28 16C27.2 18.8 24.4 20.6 21.4 20.2C18.6 19.8 17 16.2 17.6 13.4Z',
    ),
    line('M21.6 20.2C22.8 19.2 23.4 17.8 23.2 16.4M24.8 19.2C25.8 18.2 26.2 16.8 26 15.6', 1.2),
    fill(P.skin, pill(22.2, 14.4, 17.2, 18.8, 1.5), { w: 1.4 }),
    fill(P.skin, pill(22.2, 11.8, 18.6, 14, 1.3), { w: 1.4 }),
  ],
} satisfies Record<string, IconDef>;

/** The cartwheel: an upside-down star jump, drawn upright, then shrunk and tipped over. */
function cartwheel(): IconDef {
  const T = (x: number, y: number): number[] => {
    const [a, b] = turn(16 + (x - 16) * 0.86, 16 + (y - 16) * 0.86, 22);
    return [a, b + 0.4];
  };
  const hip = T(16, 12.4);
  const sh = T(16, 18.6);
  const head = T(16, 22.8);
  const limbs = [
    [P.skin, 2.6, ...hip, ...T(8.6, 4.8)],
    [P.skin, 2.6, ...hip, ...T(23.4, 4.8)],
    [P.skin, 2.4, ...sh, ...T(9.4, 26.6)],
    [P.skin, 2.4, ...sh, ...T(22.6, 26.6)],
    [P.hotPink, 4.6, ...hip, ...sh],
  ] as const;
  return [
    line('M4.6 11.4Q6.6 6.8 11 4.6', 1.5, P.gray),
    line('M27.4 20.6Q25.4 25.2 21 27.4', 1.5, P.gray),
    ...limbs.map(([, w, ...p]) => line(polyline(...p), w + 3.2)),
    ...limbs.map(([c, w, ...p]) => line(polyline(...p), w, c)),
    fill(P.skin, circle(head[0], head[1], 3)),
  ];
}
